
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Application } from '@/types/database.types';
import type { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { analyzeWorkEmail } from '@/utils/companyEmailAnalysis';
import { useEffect } from 'react';

interface PrimaryContactFormProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const PrimaryContactForm = ({ register, setValue, watch, onBlur }: PrimaryContactFormProps) => {
  const email = watch('email');
  const companyLegalName = watch('company_legal_name');
  const companyWebsite = watch('company_website');

  // Watch for email changes and analyze work email domains
  useEffect(() => {
    if (!email) return;

    console.log('Analyzing email:', email);
    console.log('Current company name:', companyLegalName);
    console.log('Current website:', companyWebsite);

    const emailInfo = analyzeWorkEmail(email);
    console.log('Email analysis result:', emailInfo);

    if (emailInfo && emailInfo.isWorkEmail) {
      if (emailInfo.suggestedCompanyName && !companyLegalName) {
        console.log('Setting company name to:', emailInfo.suggestedCompanyName);
        setValue('company_legal_name', emailInfo.suggestedCompanyName, {
          shouldDirty: true,
          shouldTouch: true
        });
      }

      if (emailInfo.suggestedWebsite && !companyWebsite) {
        console.log('Setting website to:', emailInfo.suggestedWebsite);
        setValue('company_website', emailInfo.suggestedWebsite, {
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }
  }, [email, setValue, companyLegalName, companyWebsite]);

  // Handle email blur event specifically
  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    console.log('Email blur event triggered');
    onBlur(e);
  };

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name</Label>
          <Input
            id="first_name"
            {...register('first_name')}
            placeholder="Enter your first name"
            onBlur={onBlur}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name</Label>
          <Input
            id="last_name"
            {...register('last_name')}
            placeholder="Enter your last name"
            onBlur={onBlur}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">Role Title</Label>
        <Input
          id="title"
          {...register('title')}
          placeholder="Enter your role title"
          onBlur={onBlur}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            {...register('phone')}
            type="tel"
            placeholder="Enter your phone number"
            onBlur={onBlur}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Work Email Address</Label>
          <Input
            id="email"
            {...register('email')}
            type="email"
            placeholder="Enter your work email address"
            onBlur={handleEmailBlur}
          />
        </div>
      </div>
    </>
  );
};
